import type { Longhand } from './longhand.js';
import type { WfVariantKey } from './cascade.js';
import { fontStack, type, type TypeScale } from './tokens.js';

/**
 * One class definition, declared once and compiled three ways: editor CSS, XscpData
 * `styleLess` + `variants`, and a CSS string for MCP `insert_whtml`.
 */
export interface StyleDef {
  /** e.g. `lp-h2`. Must be unique across the registry; it is the dedup key. */
  name: string;
  /** Marks a combo class (`comb: "&"` in XscpData) — only applies alongside `parent`. */
  comb?: true;
  /** For a combo, the base class name it attaches to. */
  parent?: string;
  /** Declarations at the `main` (desktop) breakpoint. */
  base: Longhand;
  /** Breakpoint and pseudo-state overrides, written as absolute values. */
  variants?: Partial<Record<WfVariantKey, Longhand>>;
}

/**
 * Properties every text-bearing class must declare explicitly.
 *
 * Our `lp-` names will not *collide* with Collctiv's 391KB stylesheet, but that sheet still
 * sets `body { font-family: Roboto; font-size: 15px }` and `h1 { font-family: Inter;
 * font-size: 44px }` — and those **inherit into** a pasted section. Leaving any of these to
 * a default means the page looks right in our preview and wrong once it lands in Webflow.
 *
 * Enforced by `defensive-completeness.test.ts`.
 */
export const REQUIRED_TEXT_PROPS = [
  'font-family',
  'font-size',
  'line-height',
  'font-weight',
  'color',
  'margin-top',
  'margin-bottom',
] as const;

/**
 * `border-box`, declared rather than assumed.
 *
 * Webflow's own `normalize.css` ships `*, *::before, *::after { box-sizing: border-box }`, so
 * a padded box behaves correctly the moment it lands in the Designer. Our editor has no such
 * global reset, which meant a padded column was `width: 100%` *plus* its padding and overflowed
 * its own grid track — adjacent columns visibly overlapping, images spilling past their cell.
 *
 * The page looked broken here and correct there, which is the drift this project exists to
 * prevent, so the declaration goes in the shared registry rather than in a stylesheet only the
 * editor loads. Every base class carries it; combos inherit it from their base.
 */
export const BOX_RESET: Longhand = {
  'box-sizing': 'border-box',
};

/** Zeroes the browser's default heading/paragraph margins so spacing is always explicit. */
export const TEXT_RESET: Longhand = {
  ...BOX_RESET,
  'margin-top': '0px',
  'margin-bottom': '0px',
};

/** Expands a type-scale step into longhand, including the font stack. */
export function font(scale: TypeScale): Longhand {
  return { 'font-family': fontStack, ...type[scale] };
}
